import { JSX, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Maximize2 } from "lucide-react"
import GroupedBarChart, {
  type GroupedBarDatum,
} from "@/components/shared/GroupedBarChart"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import type {
  CountryCoverageResponse,
  CoverageByYear,
} from "@/services/parasiteRateService"

/**
 * The coverage source categories, in left-to-right order within each year
 * cluster, with the display label and swatch colour used by the chart, the
 * legend, and the data transform.
 */
const COVERAGE_SERIES = [
  { key: "surveys", label: "Surveys", color: "#2563eb" },
  { key: "literature_review", label: "Literature review", color: "#16a34a" },
] as const

/** A coverage source-category key, derived from COVERAGE_SERIES. */
type CoverageSeriesKey = (typeof COVERAGE_SERIES)[number]["key"]

export const COVERAGE_START_YEAR: number = 2000

/**
 * Fixed horizontal distance (px) from the chart container's left edge to the
 * plot area's left edge (where the bars begin), so the 2000 column lines up
 * with the date slider's 2000 pivot. The card content is inset by its own
 * padding, so this is the target 180px-from-card-left minus that padding
 * (px-5 = 20px). Matches PIVOT_ANCHOR_PX_FROM_TRACK in DateFilterPanel.
 */
const PLOT_LEFT_ANCHOR_PX: number = 135

/** The chart's internal viewBox width, mirrored from GroupedBarChart. */
const CHART_VIEWBOX_WIDTH: number = 640

export function coverageToChartData(coverage: CountryCoverageResponse): GroupedBarDatum[] {
  const currentYear: number = new Date().getFullYear()
  const earliestYear: number = COVERAGE_START_YEAR

  // Defensive per-category maps. The service layer already normalises these,
  // but guarding here keeps the transform total for any CountryCoverageResponse
  // (including ones built directly in tests or future callers): a category that
  // is absent means zero data points of that kind, so it reads as an empty map
  // rather than throwing on `undefined[year]`.
  const surveys: CoverageByYear = coverage.surveys ?? {}
  const literatureReview: CoverageByYear = coverage.literature_review ?? {}

  // Emit a cluster for every year in the window, oldest to newest, regardless
  // of whether that year has any data — missing counts are zero-filled.
  const data: GroupedBarDatum[] = []
  for (let year = earliestYear; year <= currentYear; year++) {
    const yearKey: string = String(year)
    data.push({
      label: yearKey,
      values: {
        surveys: surveys[yearKey] ?? 0,
        literature_review: literatureReview[yearKey] ?? 0,
      },
    })
  }

  return data
}

export interface CoverageHistogramProps {
  /** The country coverage response to render as a per-year grouped histogram. */
  coverage: CountryCoverageResponse
  /** Country name shown in the full-page dialog title, or null when unknown. */
  countryName?: string | null
  /**
   * Start of the active date-range filter (year decimal, e.g. 2020.0), or null
   * when no range is applied. Year columns outside [rangeStart, rangeEnd] are
   * dimmed and non-interactive.
   */
  rangeStart?: number | null
  /** End of the active date-range filter (year decimal), or null for no range. */
  rangeEnd?: number | null
}

/**
 * CoverageHistogram renders a grouped histogram of a country's parasite rate
 * data points per start year, with the source categories (surveys / literature
 * review) drawn as side-by-side bars within each year, plus a small colour
 * legend.
 *
 * @param props - See CoverageHistogramProps.
 * @returns The histogram with a category legend, or an empty-state message.
 */
export default function CoverageHistogram({
  coverage,
  countryName = null,
  rangeStart = null,
  rangeEnd = null,
}: CoverageHistogramProps): JSX.Element {
  const data: GroupedBarDatum[] = useMemo(
    () => coverageToChartData(coverage),
    [coverage],
  )

  const seriesKeys: CoverageSeriesKey[] = COVERAGE_SERIES.map((series) => series.key)

  // Year labels (one per cluster) that fall outside the active date range, so
  // the chart can dim and disable them. A calendar year is "in range" when it
  // overlaps [rangeStart, rangeEnd] at all, i.e. floor(start) <= year <=
  // floor(end). With no range set, nothing is disabled.
  const disabledYearLabels: ReadonlySet<string> | undefined = useMemo(() => {
    if (rangeStart == null || rangeEnd == null) {
      return undefined
    }
    const firstInRange: number = Math.floor(rangeStart)
    const lastInRange: number = Math.floor(rangeEnd)
    const disabled: Set<string> = new Set<string>()
    for (const datum of data) {
      const year: number = Number(datum.label)
      if (year < firstInRange || year > lastInRange) {
        disabled.add(datum.label)
      }
    }
    return disabled
  }, [data, rangeStart, rangeEnd])

  // Rendered width (px) of the chart container, used to pin the plot-area left
  // edge to a fixed pixel offset regardless of card width. Null until measured.
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [containerWidth, setContainerWidth] = useState<number | null>(null)

  // Left offset (px) of the chart wrapper from the component's own left edge —
  // i.e. the width taken by the legend rail plus the row gap. The anchor is
  // measured from the card content's left, so this offset must be subtracted
  // from it to get the target position within the SVG itself.
  const rootRef = useRef<HTMLDivElement | null>(null)
  const [chartLeftOffset, setChartLeftOffset] = useState<number>(0)
  // Rendered width (px) of the whole component, used to switch to the narrow
  // layout (legend in a row under the chart) below NARROW_LAYOUT_PX.
  const [rootWidth, setRootWidth] = useState<number | null>(null)

  useEffect(() => {
    const element: HTMLDivElement | null = containerRef.current
    const root: HTMLDivElement | null = rootRef.current
    if (element == null || root == null) {
      return
    }

    const measure = (): void => {
      const elementRect: DOMRect = element.getBoundingClientRect()
      const rootRect: DOMRect = root.getBoundingClientRect()
      const nextContainerWidth: number = Math.round(elementRect.width)
      const nextRootWidth: number = Math.round(rootRect.width)
      const nextChartLeftOffset: number = Math.round(elementRect.left - rootRect.left)

      setContainerWidth((prev) => (prev === nextContainerWidth ? prev : nextContainerWidth))
      setRootWidth((prev) => (prev === nextRootWidth ? prev : nextRootWidth))
      setChartLeftOffset((prev) => (prev === nextChartLeftOffset ? prev : nextChartLeftOffset))
    }
    measure()
    const observer: ResizeObserver = new ResizeObserver(() => measure())
    observer.observe(element)
    observer.observe(root)
    return () => observer.disconnect()
  }, [])

  // Below this width the legend drops to a row under the histogram instead of
  // sitting as a left-hand rail, and a "full page" expand option is offered.
  const NARROW_LAYOUT_PX: number = 500
  const isNarrow: boolean = rootWidth != null && rootWidth < NARROW_LAYOUT_PX

  // The plot-area left inset (viewBox units) needed to place the plot's left
  // edge at PLOT_LEFT_ANCHOR_PX from the card content's left. The SVG starts
  // chartLeftOffset px into the row (after the legend), so the target within
  // the SVG is (anchor - chartLeftOffset) px. The SVG scales viewBox units by
  // containerWidth / CHART_VIEWBOX_WIDTH, so inset U renders at U * scale px;
  // U = targetPx / scale. Falls back to a default until the width is known.
  const plotLeftInset: number = useMemo((): number => {
    if (containerWidth == null || containerWidth <= 0) {
      return 25
    }
    const scale: number = containerWidth / CHART_VIEWBOX_WIDTH
    const targetPx: number = Math.max(PLOT_LEFT_ANCHOR_PX - chartLeftOffset, 0)
    return targetPx / scale
  }, [containerWidth, chartLeftOffset])

  const colorFor = useCallback((seriesKey: string): string => {
    const match = COVERAGE_SERIES.find((series) => series.key === seriesKey)
    return match?.color ?? "#a1a1aa"
  }, [])

  const formatSeriesLabel = useCallback((seriesKey: string): string => {
    const match = COVERAGE_SERIES.find((series) => series.key === seriesKey)
    return match?.label ?? seriesKey
  }, [])

  const hasAnyData: boolean = data.some((datum) =>
    seriesKeys.some((key) => (datum.values[key] ?? 0) > 0),
  )
  if (!hasAnyData) {
    return (
      <p className="text-xs text-muted-foreground">
        No coverage data since {COVERAGE_START_YEAR} for this country.
      </p>
    )
  }

  const legend = (
    <ul
      className={
        isNarrow
          ? "flex w-full flex-row flex-wrap justify-around gap-x-4 gap-y-1.5"
          : "flex shrink-0 flex-col gap-y-1.5"
      }
      aria-label="Coverage category legend"
    >
      {COVERAGE_SERIES.map((series) => (
        <li key={series.key} className="flex items-start gap-1.5">
          <span
            className="mt-0.5 inline-block size-2.5 shrink-0 rounded-sm"
            style={{ backgroundColor: series.color }}
            aria-hidden="true"
          />
          <span
            className={
              isNarrow
                ? "text-xs leading-tight text-muted-foreground whitespace-nowrap"
                : "max-w-16 text-xs leading-tight text-muted-foreground"
            }
          >
            {series.label}
          </span>
        </li>
      ))}
    </ul>
  )

  const chart = (
    <div ref={containerRef} className="min-w-0 flex-1">
      <GroupedBarChart
        data={data}
        seriesKeys={seriesKeys}
        colorFor={colorFor}
        formatSeriesLabel={formatSeriesLabel}
        valueUnit="data points"
        axisLabelFontSize={9}
        aspectRatio={640 / 80}
        xLabelAngle={45}
        yLabelFontSize={7}
        xLabelFontSize={7}
        maxLabelFontSizePx={12}
        minLabelFontSizePx={8}
        plotPadding={{ left: isNarrow ? 25 : plotLeftInset, right: -10, bottom: 28, top: 3 }}
        disabledLabels={disabledYearLabels}
      />
    </div>
  )

  if (isNarrow) {
    return (
      <div ref={rootRef} className="flex flex-col gap-2">
        <div className="flex items-start justify-end">
          <Dialog>
            <DialogTrigger
              aria-label="Open coverage histogram full page"
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <Maximize2 className="size-3.5" aria-hidden="true" />
              Full page
            </DialogTrigger>
            <DialogContent className="h-[95vh] w-screen max-w-[95vw] sm:max-w-none">
              <DialogHeader>
                <DialogTitle className="text-sm font-semibold uppercase tracking-wider">
                  PR coverage
                  {countryName != null && (
                    <span className="ml-1.5 font-medium normal-case tracking-normal text-muted-foreground">
                      {countryName}
                    </span>
                  )}
                </DialogTitle>
              </DialogHeader>
              <div className="flex min-h-0 flex-1 flex-col gap-3">
                <div className="min-h-0 flex-1">
                  <GroupedBarChart
                    data={data}
                    seriesKeys={seriesKeys}
                    colorFor={colorFor}
                    formatSeriesLabel={formatSeriesLabel}
                    valueUnit="data points"
                    yAxisLabel="Data points"
                    xAxisLabel="Year"
                    axisLabelFontSize={12}
                    aspectRatio={16 / 9}
                    xLabelAngle={45}
                    disabledLabels={disabledYearLabels}
                  />
                </div>
                {legend}
              </div>
            </DialogContent>
          </Dialog>
        </div>
        {chart}
        {legend}
      </div>
    )
  }

  return (
    <div ref={rootRef} className="flex flex-row items-center gap-3">
      {legend}
      {chart}
    </div>
  )
}
